// URL routing for Second Memories.
//
// The URL is the single source of truth for which page (appView) is showing
// and whether a "view" detail dialog (View Friend / Trip / Category / Tag)
// is open. Filters and other transient UI state stay in query params or in
// Redux only — they do not influence the path.
//
// Path shape: /[username]/[section]/[itemId?]
//   /joegordon                    → redirect to /joegordon/about
//   /joegordon/about              → About view
//   /joegordon/map                → Map view
//   /joegordon/timeline           → Timeline view
//   /joegordon/friends            → Friends list
//   /joegordon/friends/[id]       → Friends list with View Friend dialog
//   /joegordon/trips/[id]         → Trips list with View Trip dialog
//   /joegordon/categories/[id]    → Categories list with View Category dialog
//   /joegordon/tags/[id]          → Tags list with View Tag dialog
//
// This module is purely concerned with view/dialog routing. Filter sync is
// handled separately in App.js.

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setFilters } from '../store/Actions';

const SECTION_TO_VIEW = {
  about: 'About',
  map: 'Map',
  timeline: 'Timeline',
  friends: 'Friends',
  trips: 'Trips',
  categories: 'Categories',
  tags: 'Tags',
};

const VIEW_TO_SECTION = {
  About: 'about',
  Map: 'map',
  Timeline: 'timeline',
  Friends: 'friends',
  Trips: 'trips',
  Categories: 'categories',
  Tags: 'tags',
};

// Sections that support /[section]/[id] detail pages, plus the dialog name
// they map to and the dialogData key that holds the entity object.
const DETAIL_SECTIONS = {
  friends:    {dialog: 'View Friend',   entityKey: 'friend'},
  trips:      {dialog: 'View Trip',     entityKey: 'trip'},
  categories: {dialog: 'View Category', entityKey: 'category'},
  tags:       {dialog: 'View Tag',      entityKey: 'tag'},
};

const DIALOG_TO_ENTITY_KEY = Object.fromEntries(
  Object.entries(DETAIL_SECTIONS).map(([, info]) => [info.dialog, info.entityKey])
);

const VIEW_DIALOGS = new Set(Object.values(DETAIL_SECTIONS).map(info => info.dialog));

// Search params we keep when changing sections. Filter params
// (categories, friends, trips, tags, startDate, endDate) are page-scoped and
// get stripped on section transitions; meta params persist.
const META_SEARCH_KEYS = ['embed', 'hideHeader'];

// Tabs available on the entity detail pages
// (/[username]/[section]/[id]/[tab]). 'summary' is the default and is also
// what we render when no tab segment is present.
export const DETAIL_TABS = ['summary', 'events'];
export const DEFAULT_DETAIL_TAB = 'summary';

// Parse `/username/section/itemId/tab` into a structured route descriptor.
// Falls back to About (the default page) when the section is missing or the
// section string isn't one we recognize, so a stray URL still lands on a
// real page rather than blanking the app. The 4th /tab segment is only
// meaningful for detail routes; on list/about pages it's ignored.
export const parsePath = (pathname) => {
  const parts = (pathname || '').split('/').filter(Boolean);
  const username = parts[0] ? decodeURIComponent(parts[0]) : null;
  const sectionRaw = parts[1] ? parts[1].toLowerCase() : null;
  const itemId = parts[2] ? decodeURIComponent(parts[2]) : null;
  const tabRaw = parts[3] ? parts[3].toLowerCase() : null;

  if(!username){
    return {
      username: null, view: 'About',
      viewDialog: null, viewDialogId: null, viewDialogTab: DEFAULT_DETAIL_TAB,
    };
  }

  const view = sectionRaw ? (SECTION_TO_VIEW[sectionRaw] || 'About') : 'About';
  const detail = sectionRaw ? DETAIL_SECTIONS[sectionRaw] : null;

  if(detail && itemId){
    const tab = DETAIL_TABS.includes(tabRaw) ? tabRaw : DEFAULT_DETAIL_TAB;
    return {
      username, view,
      viewDialog: detail.dialog, viewDialogId: itemId, viewDialogTab: tab,
    };
  }

  return {
    username, view,
    viewDialog: null, viewDialogId: null, viewDialogTab: DEFAULT_DETAIL_TAB,
  };
};

// Build the canonical pathname for a section list page.
export const buildSectionPath = (username, view) => {
  if(!username) return '/';
  const section = VIEW_TO_SECTION[view] || 'about';
  return `/${encodeURIComponent(username)}/${section}`;
};

// Build the canonical pathname for a section's detail page, optionally
// scoped to a specific tab. Omits the tab segment for the default tab so
// the canonical URL stays clean. Falls back to the section list path if
// either of the two leading args is missing.
export const buildDetailPath = (username, view, entityId, tab) => {
  if(!username || !entityId) return buildSectionPath(username, view);
  const section = VIEW_TO_SECTION[view];
  if(!section) return buildSectionPath(username, view);
  const base = `/${encodeURIComponent(username)}/${section}/${encodeURIComponent(entityId)}`;
  if(tab && tab !== DEFAULT_DETAIL_TAB && DETAIL_TABS.includes(tab)){
    return `${base}/${tab}`;
  }
  return base;
};

// The section slug for a given pathname (e.g. 'map'), or '' for the bare
// /[username] root.
export const getSectionFromPath = (pathname) => {
  const parts = (pathname || '').split('/').filter(Boolean);
  return parts[1] ? parts[1].toLowerCase() : '';
};

// Strip filter params from a search string, preserving meta params.
// Returns a string suitable for passing to navigate({search}) — empty string
// for "no params", or a leading-? string otherwise.
export const stripFilterSearch = (search) => {
  const oldParams = new URLSearchParams(search || '');
  const preserved = new URLSearchParams();
  META_SEARCH_KEYS.forEach(key => {
    const value = oldParams.get(key);
    if(value !== null) preserved.set(key, value);
  });
  const result = preserved.toString();
  return result ? `?${result}` : '';
};

// True if the dialog name is one of the four URL-routed detail dialogs.
export const isViewDialog = (dialog) => VIEW_DIALOGS.has(dialog);

// Look up the entity object for a (dialog, id) pair from the right slice of
// Redux state. Returns null when the id isn't found.
export const resolveEntity = (dialog, id, {friends, trips, categories, tags}) => {
  switch(dialog){
    case 'View Friend':   return friends?.[id] || null;
    case 'View Trip':     return trips?.[id] || null;
    case 'View Category': return categories?.[id] || null;
    case 'View Tag':      return tags?.[id] || null;
    default: return null;
  }
};

export const ENTITY_KEY_BY_DIALOG = DIALOG_TO_ENTITY_KEY;

// Hook that returns a navigation function with built-in section-change
// awareness. Calling it with a target pathname:
//   - if the target is in the same section as the current pathname, the
//     existing search string is preserved (e.g. /friends → /friends/[id]
//     keeps any open filters);
//   - if the target is in a different section, filter Redux state is
//     cleared and filter params are stripped from the new URL, leaving only
//     meta params like embed/hideHeader.
//
// All view/dialog navigation in the app should go through this rather than
// dispatching setAppView / setDialogOpen('View X', ...) directly. The URL
// becomes the source of truth and the URL→Redux effect in App.js handles
// updating Redux to match.
export const useAppNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  return useCallback((targetPath, options) => {
    const currentSection = getSectionFromPath(location.pathname);
    const targetSection = getSectionFromPath(targetPath);
    const sectionChanged = currentSection !== targetSection;

    if(sectionChanged){
      // Clear filter Redux state so it doesn't bleed across pages, and strip
      // filter params from the URL we navigate to so the filter→URL effect
      // on the next render doesn't re-derive them from the previous search.
      dispatch(setFilters([], [], [], [], null, null));
      navigate({
        pathname: targetPath,
        search: stripFilterSearch(location.search),
      }, options);
      return;
    }

    navigate({
      pathname: targetPath,
      search: location.search,
    }, options);
  }, [dispatch, location.pathname, location.search, navigate]);
};
